import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useDelight } from '@/hooks/useDelight';
import { queryKeys } from '@/lib/query-keys';
import {
  healthToStatus,
  statusToClickupColor,
} from './useProjectsV2.helpers';
import type {
  DashboardSummary,
  HealthSnapshot,
  HealthStatus,
  HealthTrendPoint,
  LinkedContract,
  OverdueTask,
  ProjectStatus,
  ProjectWithClient,
  ProjectWithMetrics,
  TaskMetrics,
} from './useProjectsV2.types';
export type {
  DashboardSummary,
  HealthSnapshot,
  HealthStatus,
  HealthTrendPoint,
  LinkedContract,
  OverdueTask,
  ProjectStatus,
  ProjectV2,
  ProjectWithClient,
  ProjectWithMetrics,
  TaskMetrics,
} from './useProjectsV2.types';
export {
  clickupColorToStatus,
  getHealthBgColor,
  getHealthColor,
  getHealthLabel,
  getStatusBgColor,
  getStatusColor,
  getStatusLabel,
  healthToStatus,
  PROJECT_STATUS_CONFIG,
  statusToClickupColor,
  statusToHealthColor,
} from './useProjectsV2.helpers';

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch all projects with their task metrics
 */
export function useProjectsV2(clientId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { celebrateGrowth } = useDelight();

  const projectsQuery = useQuery({
    queryKey: queryKeys.projectsV2.byClient(clientId),
    queryFn: async () => {
      // Step 1: Fetch all projects in a single query
      let query = supabase
        .from('projects')
        .select('*, clients(id, name)')
        .eq('is_active', true)
        .eq('is_archived', false)
        .order('created_at', { ascending: false });

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const projects = data as ProjectWithClient[];

      // If no projects, return early
      if (!projects.length) {
        return [];
      }

      // Step 2: Batch fetch all task metrics in a single query (fixes N+1)
      const projectIds = projects.map((p) => p.id);
      const { data: batchMetrics, error: metricsError } = await supabase.rpc(
        'get_batch_project_task_metrics',
        { p_project_ids: projectIds }
      );

      if (metricsError) {
        console.error('Error fetching batch metrics:', metricsError);
      }

      // Create a lookup map for O(1) access
      const metricsMap = new Map<string, TaskMetrics>();
      if (batchMetrics) {
        for (const row of batchMetrics) {
          metricsMap.set(row.project_id, {
            total: Number(row.total) || 0,
            completed: Number(row.completed) || 0,
            overdue: Number(row.overdue) || 0,
            stuck: Number(row.stuck) || 0,
            in_progress: Number(row.in_progress) || 0,
            upcoming: Number(row.upcoming) || 0,
          });
        }
      }

      // Step 3: Combine projects with their metrics
      const defaultMetrics: TaskMetrics = {
        total: 0,
        completed: 0,
        overdue: 0,
        stuck: 0,
        in_progress: 0,
        upcoming: 0,
        active: 0,
        with_client: 0,
        backlog: 0,
      };

      const projectsWithMetrics: ProjectWithMetrics[] = projects.map((project) => {
        const metrics = metricsMap.get(project.id) || defaultMetrics;

        // Determine effective status - use project_status if set, otherwise derive from health
        const effectiveStatus: ProjectStatus = project.project_status ||
          healthToStatus(project.health_override || project.current_health);

        return {
          ...project,
          task_metrics: metrics,
          effective_health: (project.health_override || project.current_health) as HealthStatus,
          effective_status: effectiveStatus,
        };
      });

      return projectsWithMetrics;
    },
    staleTime: 1000 * 60, // 1 minute
  });

  const createProject = useMutation({
    mutationFn: async ({
      clientId,
      name,
      description,
      clickupListId,
      clickupListName,
    }: {
      clientId: string;
      name: string;
      description?: string;
      clickupListId: string;
      clickupListName: string;
    }) => {
      const { data, error } = await supabase
        .from('projects')
        .insert({
          client_id: clientId,
          name,
          description,
          source: 'clickup_live',
          clickup_list_id: clickupListId,
          clickup_list_name: clickupListName,
          current_health: 'unknown',
          project_status: 'on_track',
          is_active: true,
          project_data: [], // Empty - tasks come from synced_tasks
          task_count: 0,
        })
        .select('*, clients(id, name)')
        .single();

      if (error) throw error;

      // Link existing synced tasks to this project
      await supabase.rpc('link_synced_tasks_to_projects');

      return data as ProjectWithClient;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.byClient(data.client_id) });
      toast({ title: 'Project created', description: `Linked to ${data.clickup_list_name}` });
      celebrateGrowth('project');
    },
    onError: (error) => {
      toast({ title: 'Error creating project', description: error.message, variant: 'destructive' });
    },
  });

  const archiveProject = useMutation({
    mutationFn: async (projectId: string) => {
      const { error } = await supabase
        .from('projects')
        .update({ is_active: false })
        .eq('id', projectId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.all });
      toast({ title: 'Project archived' });
    },
  });

  return {
    projects: projectsQuery.data || [],
    isLoading: projectsQuery.isLoading,
    error: projectsQuery.error,
    createProject,
    archiveProject,
    refetch: projectsQuery.refetch,
  };
}

/**
 * Fetch a single project with full details
 */
export function useProjectV2(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.projectsV2.detail(projectId),
    queryFn: async () => {
      if (!projectId) return null;

      const { data, error } = await supabase
        .from('projects')
        .select('*, clients(id, name)')
        .eq('id', projectId)
        .single();

      if (error) throw error;

      // Get task metrics
      const { data: metricsData } = await supabase.rpc('get_project_task_metrics', {
        p_project_id: projectId,
      });

      const project = data as ProjectWithClient;
      const metrics: TaskMetrics = metricsData || {
        total: 0,
        completed: 0,
        overdue: 0,
        stuck: 0,
        in_progress: 0,
        upcoming: 0,
      };

      const effectiveStatus: ProjectStatus = project.project_status ||
        healthToStatus(project.health_override || project.current_health);

      return {
        ...project,
        task_metrics: metrics,
        effective_health: (project.health_override || project.current_health) as HealthStatus,
        effective_status: effectiveStatus,
      } as ProjectWithMetrics;
    },
    enabled: !!projectId,
  });
}

/**
 * Fetch synced tasks for a project
 */
export function useProjectTasks(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.projectsV2.tasks(projectId),
    queryFn: async () => {
      if (!projectId) return [];

      const { data, error } = await supabase
        .from('clickup_synced_tasks')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_deleted', false)
        .order('date_updated', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useClientOverdueTasks(clientId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.projectsV2.overdueTasks(clientId),
    queryFn: async () => {
      if (!clientId) return [];

      // Use midnight UTC of today's Central Time date as boundary
      // (ClickUp stores date-level due dates near midnight UTC)
      const now = new Date();
      const centralNow = new Date(
        now.toLocaleString('en-US', { timeZone: 'America/Chicago' }),
      );
      const y = centralNow.getFullYear();
      const m = String(centralNow.getMonth() + 1).padStart(2, '0');
      const d = String(centralNow.getDate()).padStart(2, '0');
      const todayUTC = `${y}-${m}-${d}T00:00:00.000Z`;

      // Fetch tasks that are overdue (due_date before today) and NOT completed
      const { data, error } = await supabase
        .from('clickup_synced_tasks')
        .select('id, clickup_task_id, project_id, name, due_date, status_name, status_type, date_closed')
        .eq('client_id', clientId)
        .eq('is_deleted', false)
        .not('due_date', 'is', null)
        .lt('due_date', todayUTC)
        .is('date_closed', null)
        .not('status_type', 'in', '("closed","done")')
        .order('due_date', { ascending: true })
        .limit(50);

      if (error) throw error;

      // Exclude non-actionable statuses (on hold, backlog, etc.)
      const NON_ACTIONABLE = [
        'on hold', 'on-hold', 'backlog', 'icebox', 'someday',
        'blocked', 'deferred', 'paused', 'suspended', 'waiting', 'pending',
        'complete', 'completed', 'done', 'closed',
      ];
      const filtered = (data || []).filter((t) => {
        const lower = ((t as { status_name?: string }).status_name || '').toLowerCase().trim();
        return !NON_ACTIONABLE.some((p) => lower.includes(p));
      });
      return filtered as OverdueTask[];
    },
    enabled: !!clientId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch health history for a project (for charts)
 */
export function useProjectHealthHistory(projectId: string | undefined, weeks: number = 12) {
  return useQuery({
    queryKey: queryKeys.projectsV2.healthHistory(projectId, weeks),
    queryFn: async () => {
      if (!projectId) return [];

      const { data, error } = await supabase.rpc('get_project_health_history', {
        p_project_id: projectId,
        p_weeks: weeks,
      });

      if (error) throw error;
      return (data || []) as HealthSnapshot[];
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch aggregated health trend for dashboard chart
 */
export function useProjectsHealthTrend(weeks: number = 12) {
  return useQuery({
    queryKey: queryKeys.projectsV2.healthTrend(weeks),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_projects_health_trend', {
        p_weeks: weeks,
      });

      if (error) throw error;
      return (data || []) as HealthTrendPoint[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch dashboard summary metrics
 */
export function useProjectsDashboard() {
  return useQuery({
    queryKey: queryKeys.projectsV2.dashboard,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_projects_dashboard_summary');

      if (error) throw error;
      return (data || {
        total_projects: 0,
        active_projects: 0,
        green_count: 0,
        yellow_count: 0,
        red_count: 0,
        unknown_count: 0,
        total_tasks: 0,
        overdue_tasks: 0,
        clients_with_projects: 0,
      }) as DashboardSummary;
    },
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Update project health (manual override) - legacy
 */
export function useUpdateProjectHealth() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      healthOverride,
      healthNotes,
    }: {
      projectId: string;
      healthOverride: HealthStatus | null;
      healthNotes?: string;
    }) => {
      const { error } = await supabase
        .from('projects')
        .update({
          health_override: healthOverride,
          health_notes: healthNotes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', projectId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.dashboard });
      toast({ title: 'Health updated' });
    },
    onError: (error) => {
      toast({ title: 'Error updating health', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * Update project status (new system)
 */
export function useUpdateProjectStatus() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      status,
      syncToClickUp = false,
    }: {
      projectId: string;
      status: ProjectStatus;
      syncToClickUp?: boolean;
    }) => {
      // Update local status
      const { error } = await supabase
        .from('projects')
        .update({
          project_status: status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', projectId);

      if (error) throw error;

      // Optionally sync to ClickUp
      if (syncToClickUp) {
        // Get project to find clickup_list_id
        const { data: project } = await supabase
          .from('projects')
          .select('clickup_list_id')
          .eq('id', projectId)
          .single();

        if (project?.clickup_list_id) {
          // Call edge function to update ClickUp list status
          const { error: syncError } = await supabase.functions.invoke('clickup-sync', {
            body: {
              action: 'update-list-status',
              list_id: project.clickup_list_id,
              status_color: statusToClickupColor(status),
            },
          });

          if (syncError) {
            console.error('Failed to sync to ClickUp:', syncError);
            // Don't throw - local update succeeded
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.dashboard });
      toast({ title: 'Status updated' });
    },
    onError: (error) => {
      toast({ title: 'Error updating status', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * Create a health snapshot for a project
 */
export function useCreateHealthSnapshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      weekEnding,
    }: {
      projectId: string;
      weekEnding?: string;
    }) => {
      const { data, error } = await supabase.rpc('create_project_health_snapshot', {
        p_project_id: projectId,
        p_week_ending: weekEnding || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.healthHistory() });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.healthTrend() });
    },
  });
}

/**
 * Recalculate health for a project (from tasks)
 */
export function useRecalculateProjectHealth() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      // Get calculated health
      const { data: health, error: healthError } = await supabase.rpc('calculate_project_health', {
        p_project_id: projectId,
      });

      if (healthError) throw healthError;

      // Update project's current_health (not override)
      const { error: updateError } = await supabase
        .from('projects')
        .update({
          current_health: health,
          updated_at: new Date().toISOString(),
        })
        .eq('id', projectId);

      if (updateError) throw updateError;

      return health;
    },
    onSuccess: (health) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.detail() });
      toast({ title: 'Health recalculated', description: `Project is ${health}` });
    },
    onError: (error) => {
      toast({ title: 'Error recalculating health', description: error.message, variant: 'destructive' });
    },
  });
}

// =============================================================================
// Contract Linking Hooks
// =============================================================================

/**
 * Fetch linked contracts for a project
 */
export function useProjectContracts(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.projectsV2.contracts(projectId),
    queryFn: async () => {
      if (!projectId) return [];

      const { data, error } = await supabase
        .from('project_contracts')
        .select(`
          id,
          document_id,
          created_at,
          contractual_documents (
            id,
            name,
            document_type
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as LinkedContract[];
    },
    enabled: !!projectId,
  });
}

/**
 * Link a contract to a project
 */
export function useLinkContract() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
    }: {
      projectId: string;
      documentId: string;
    }) => {
      const { data, error } = await supabase
        .from('project_contracts')
        .insert({
          project_id: projectId,
          document_id: documentId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.contracts(projectId) });
      toast({ title: 'Contract linked' });
    },
    onError: (error: unknown) => {
      const errorWithCode = error as { code?: string; message?: string };
      if (errorWithCode.code === '23505') {
        toast({ title: 'Already linked', description: 'This contract is already linked to this project', variant: 'destructive' });
      } else {
        toast({ title: 'Error linking contract', description: errorWithCode.message ?? 'Unknown error', variant: 'destructive' });
      }
    },
  });
}

/**
 * Unlink a contract from a project
 */
export function useUnlinkContract() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
    }: {
      projectId: string;
      documentId: string;
    }) => {
      const { error } = await supabase
        .from('project_contracts')
        .delete()
        .eq('project_id', projectId)
        .eq('document_id', documentId);

      if (error) throw error;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.contracts(projectId) });
      toast({ title: 'Contract unlinked' });
    },
    onError: (error) => {
      toast({ title: 'Error unlinking contract', description: error.message, variant: 'destructive' });
    },
  });
}
